<template>
  <div ref="container" />
</template>

<script lang="ts">
// Privacy/tracking blockers can silently drop the Cloudflare request, in which
// case neither `load` nor `error` ever fires and the widget hangs invisibly.
// Time out so the caller can surface an actionable message instead of a blank box.
const LOAD_TIMEOUT_MS = 10000;
let loadPromise: Promise<void> | null = null;
function loadScript(): Promise<void> {
  if (loadPromise) {
    return loadPromise;
  }

  loadPromise = new Promise<void>((resolve, reject) => {
    const fail = (reason: string) => {
      // Reset so a remount (e.g. after the visitor disables their blocker) retries.
      loadPromise = null;
      reject(new Error(reason));
    };
    const timeout = setTimeout(
      () => fail("turnstile-load-timeout"),
      LOAD_TIMEOUT_MS
    );
    const succeed = () => {
      clearTimeout(timeout);
      resolve();
    };

    const existing = document.querySelector(
      'script[src*="challenges.cloudflare.com/turnstile"]'
    );

    if (existing) {
      if (window.turnstile) {
        succeed();
        return;
      }
      // Script tag exists but hasn't loaded yet
      existing.addEventListener("load", succeed);
      existing.addEventListener("error", () => fail("turnstile-script-error"));
      return;
    }

    window.onloadTurnstileCallback = succeed;
    const script = document.createElement("script");
    script.src =
      "https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onloadTurnstileCallback";
    script.async = true;
    script.defer = true;
    script.onerror = () => fail("turnstile-script-error");

    document.head.appendChild(script);
  });

  return loadPromise;
}
</script>

<script setup lang="ts">
import { useTemplateRef, watch } from "vue";

const props = defineProps<{
  action?: string;
  cData?: string;
  execution?: "render" | "execute";
  theme?: "light" | "dark" | "auto";
  language?: string;
  tabindex?: number;
  size?: "normal" | "flexible" | "compact";
  retry?: "auto" | "never";
  retryInterval?: number;
  refreshExpired?: "auto" | "manual" | "never";
  refreshTimeout?: "auto" | "manual" | "never";
  appearance?: "always" | "execute" | "interaction-only";
  feedbackEnabled?: boolean;
}>();

const emit = defineEmits<{
  callback: [response: string];
  errorCallback: [response: string];
  expiredCallback: [];
  loadError: [];
}>();

const container = useTemplateRef("container");

watch(container, async (node, _prevNode, onCleanup) => {
  if (!node) {
    return;
  }

  const controller = new AbortController();
  const signal = controller.signal;

  onCleanup(() => {
    controller.abort();
  });

  try {
    await loadScript();
  } catch {
    if (!signal.aborted) {
      emit("loadError");
    }
    return;
  }
  if (signal.aborted) {
    return;
  }

  const id = window.turnstile!.render(node, {
    sitekey: "0x4AAAAAABrWC8POBFndQnsz",
    callback: (response: string) => emit("callback", response),
    "expired-callback": () => emit("expiredCallback"),
    "error-callback": (response: string) => emit("errorCallback", response),
    action: props.action,
    cData: props.cData,
    execution: props.execution ?? "render",
    theme: props.theme ?? "auto",
    language: props.language ?? "auto",
    tabindex: props.tabindex ?? 0,
    size: props.size,
    retry: props.retry,
    "retry-interval": props.retryInterval,
    "refresh-expired": props.refreshExpired,
    "refresh-timeout": props.refreshTimeout,
    appearance: props.appearance,
    "feedback-enabled": props.feedbackEnabled,
  });

  signal.addEventListener("abort", () => {
    window.turnstile!.remove(id);
  });
});
</script>
